import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Modal, Tag, message, Alert, Empty, Row, Col, Input, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ClockCircleOutlined, RobotOutlined } from '@ant-design/icons';
import apiClient from '../api';

const GroupReplyRulesManager = ({ templates, loading, onUpdate }) => {
  const [rules, setRules] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    groupId: '',
    groupName: '',
    templateId: '',
    replyIfNoResponseWithin: null,
    isActive: true
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      const response = await apiClient.get('/group-reply-rules');
      if (response.data.success) {
        setRules(response.data.rules || []);
      }
    } catch (error) {
      console.error('Error loading group reply rules:', error);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setFormData({ 
      groupId: '', 
      groupName: '', 
      templateId: '', 
      replyIfNoResponseWithin: null,
      isActive: true 
    });
    setIsModalVisible(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      groupId: rule.groupId,
      groupName: rule.groupName || '',
      templateId: rule.templateId,
      replyIfNoResponseWithin: rule.replyIfNoResponseWithin,
      isActive: rule.isActive !== undefined ? rule.isActive : true
    });
    setIsModalVisible(true);
  };

  const handleDelete = (rule) => {
    if (!rule) return;
    console.log('[GroupReplyRulesManager] Delete clicked for rule:', rule);
    setDeleteTarget(rule);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const groupId = deleteTarget.groupId;
    if (!groupId) return;

    try {
      const response = await apiClient.delete(
        `/group-reply-rules/${encodeURIComponent(groupId)}`
      );
      if (response.data.success) {
        message.success('Group reply rule deleted successfully');
        setDeleteTarget(null);
        loadRules();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to delete rule');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete rule');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.groupId || !formData.templateId) {
      message.error('Please fill in all required fields');
      return;
    }

    try {
      const url = editingRule 
        ? `/group-reply-rules/${encodeURIComponent(editingRule.groupId)}`
        : '/group-reply-rules';
      
      const method = editingRule ? 'put' : 'post';
      const response = await apiClient[method](url, formData);

      if (response.data.success) {
        message.success(`Group reply rule ${editingRule ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        loadRules();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to save rule');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save rule');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Group ID',
      dataIndex: 'groupId',
      key: 'groupId',
      width: 300,
      render: (text) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#667eea' }} />
          <Tag color="blue" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            {text.length > 30 ? `${text.substring(0, 30)}...` : text}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Group Name',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 200,
      render: (text) => text || <Tag color="default">Unnamed</Tag>,
    },
    {
      title: 'Agent',
      dataIndex: 'templateId',
      key: 'templateId',
      render: (templateId) => {
        const template = templates.find(t => t.templateId === templateId);
        return (
          <Space direction="vertical" size={2}>
            <Tag color="green" icon={<RobotOutlined />}>
              {templateId}
            </Tag>
            {template && (
              <span style={{ fontSize: 12, color: '#666' }}>
                {template.agentName}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Reply Condition',
      dataIndex: 'replyIfNoResponseWithin',
      key: 'replyIfNoResponseWithin',
      width: 200,
      render: (minutes) => {
        if (minutes === null || minutes === undefined) {
          return <Tag color="blue">Always reply</Tag>;
        }
        return (
          <Tag color="orange" icon={<ClockCircleOutlined />}>
            Reply if no response in {minutes} min
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>⏰ Group Reply Rules</h2>
            <p style={{ color: '#666', margin: '4px 0 0 0' }}>
              Set conditional reply rules for WhatsApp groups (e.g., reply only if no one responds within X minutes)
            </p>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              size="large"
              onClick={handleCreate}
              disabled={templates.length === 0}
            >
              Create Rule
            </Button>
          </Col>
        </Row>
      </div>

      <Alert
        message="How it works"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>Set conditional reply rules for groups</strong>
            </p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li><strong>Always reply:</strong> Bot replies immediately to all messages</li>
              <li><strong>Reply if no response in X min:</strong> Bot waits X minutes, only replies if no one else did</li>
            </ul>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              💡 Example: Set "10 minutes" - Bot will only reply if no team member responds within 10 minutes
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Group Reply Rule"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>Are you sure you want to remove this group reply rule? This action cannot be undone.</p>
        {deleteTarget && (
          <p>
            <strong>Group ID:</strong>{' '}
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {deleteTarget.groupId}
            </Tag>
          </p>
        )}
      </Modal>

      {templates.length === 0 && (
        <Alert
          message="No Templates Available"
          description="You need to create at least one agent template before creating reply rules."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table 
        columns={columns} 
        dataSource={rules}
        rowKey="groupId"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <Empty 
              description="No reply rules yet. Create conditional reply rules for groups!"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      <Modal
        title={editingRule ? "✏️ Edit Group Reply Rule" : "➕ Create Group Reply Rule"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Group ID <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              placeholder="e.g., 120363123456789012@g.us"
              value={formData.groupId}
              onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
              disabled={!!editingRule}
            />
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Format: WhatsApp group ID with @g.us suffix
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Group Name (Optional)
            </label>
            <Input
              placeholder="e.g., Customer Support Team"
              value={formData.groupName}
              onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Agent Template <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              style={{ width: '100%', padding: '8px', fontSize: 14, borderRadius: 4, border: '1px solid #d9d9d9' }}
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
            >
              <option value="">Select an agent template</option>
              {templates.map(template => (
                <option key={template.templateId} value={template.templateId}>
                  {template.templateId} - {template.agentName}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Reply Condition
            </label>
            <Input
              type="number"
              placeholder="Leave empty for always reply, or enter minutes (e.g., 10)"
              value={formData.replyIfNoResponseWithin || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  replyIfNoResponseWithin: value === '' ? null : parseInt(value) 
                });
              }}
            />
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Leave empty = Always reply immediately. Enter number = Reply only if no one responds within X minutes
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Active Status
            </label>
            <Switch
              checked={formData.isActive}
              onChange={(checked) => setFormData({ ...formData, isActive: checked })}
            />
            <span style={{ marginLeft: 8 }}>{formData.isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GroupReplyRulesManager;

